/**
 * Normalización de señales de identidad de un negocio (FASE 2.6, acuerdo Sales/Prospect).
 * Espejo exacto de las funciones SQL `normalizar_email`, `normalizar_dominio_web` y
 * `normalizar_telefono_e164` de supabase/migrations/20260926090000_nevora_prospect_senales.sql:
 * si cambia una, cambia la otra (los tests de este fichero son la referencia).
 */

/** Email en minúsculas y sin espacios; null si queda vacío o no parece un email. */
export const normalizarEmail = (email?: string | null): string | null => {
  const e = (email ?? "").trim().toLowerCase().replace(/\s+/g, "");
  if (!e) return null;
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e) ? e : null;
};

/** Proveedores de correo genérico y plataformas: sus dominios no identifican al negocio. */
export const DOMINIOS_EXCLUIDOS = [
  "gmail.com",
  "googlemail.com",
  "hotmail.com",
  "hotmail.es",
  "outlook.com",
  "outlook.es",
  "live.com",
  "yahoo.com",
  "yahoo.es",
  "icloud.com",
  "me.com",
  "facebook.com",
  "instagram.com",
  "linktr.ee",
  "wixsite.com",
  "wix.com",
  "sites.google.com",
] as const;

const esDominioExcluido = (dominio: string) =>
  DOMINIOS_EXCLUIDOS.some((d) => dominio === d || dominio.endsWith(`.${d}`));

/**
 * Dominio web normalizado: minúsculas, sin protocolo, sin `www.`, sin ruta ni puerto.
 * Devuelve null si está vacío, no es un dominio o es un correo genérico / plataforma.
 * Ej.: "https://www.Bar-Pepe.com/carta" → "bar-pepe.com"; "facebook.com/barpepe" → null.
 */
export const normalizarDominioWeb = (web?: string | null): string | null => {
  let d = (web ?? "").trim().toLowerCase();
  if (!d) return null;
  d = d.replace(/^[a-z][a-z0-9+.-]*:\/\//, ""); // protocolo
  d = d.replace(/^www\./, "");
  d = d.split(/[/?#]/)[0]; // ruta, query, ancla
  d = d.split("@").pop() ?? d; // por si pegan un email
  d = d.split(":")[0]; // puerto
  d = d.replace(/\.+$/, "");
  if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(d)) return null;
  if (esDominioExcluido(d)) return null;
  return d;
};

/**
 * Teléfono en formato E.164 (+34612345678), España por defecto. Acepta espacios, guiones,
 * paréntesis, prefijo "00" y "+". Null si no hay entre 7 y 15 dígitos.
 */
export const normalizarTelefonoE164 = (
  telefono?: string | null,
): string | null => {
  const t = (telefono ?? "").trim();
  if (!t) return null;
  let digitos = t.replace(/\D/g, "");
  if (!digitos) return null;
  const internacional = t.startsWith("+") || digitos.startsWith("00");
  if (digitos.startsWith("00")) digitos = digitos.slice(2);
  if (!internacional && digitos.length === 9) digitos = `34${digitos}`;
  if (digitos.length < 7 || digitos.length > 15) return null;
  return `+${digitos}`;
};
